import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../components/ui';
import { Button, Card, Badge, Input, Modal, Spinner } from '../../components/ui';
import {
  Plus, CheckCircle, Trash2, Edit2, X, FileText,
  AlertCircle, Clock, Search, RefreshCw, Copy,
  Sparkles, Tag, Globe, Shield, Zap, Lock
} from 'lucide-react';
import './Templates.css';

const LANGUAGE_OPTIONS = [
  { value: 'en_US', label: 'English (US)' },
  { value: 'en_GB', label: 'English (UK)' },
  { value: 'sw', label: 'Swahili' },
  { value: 'fr', label: 'French' },
  { value: 'ar', label: 'Arabic' },
  { value: 'es', label: 'Spanish' },
  { value: 'pt_PT', label: 'Portuguese' },
];

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [syncingId, setSyncingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    category: 'UTILITY',
    content: '',
    language: 'en_US'
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { request } = useApi();
  const { success, error: toastError } = useToast();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await request('get', '/api/templates');
      setTemplates(data || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditing(null);
    setForm({ name: '', category: 'UTILITY', content: '', language: 'en_US' });
    setError('');
    setShowModal(true);
  };

  const openEditModal = (template) => {
    if (template.status === 'APPROVED') {
      toastError("Meta doesn't allow editing an already-approved template. Create a new one instead.");
      return;
    }
    setEditing(template);
    setForm({
      name: template.name,
      category: template.category,
      content: template.content,
      language: template.language || 'en_US'
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      if (editing) {
        await request('put', `/api/templates/${editing.id}`, form);
        success('Template updated and resubmitted to Meta for review.');
      } else {
        await request('post', '/api/templates', form);
        success('Template submitted to Meta for review.');
      }
      setShowModal(false);
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save template');
    } finally {
      setSubmitting(false);
    }
  };

  // Pulls the live status from Meta — use this instead of "simulate approve"
  const handleSync = async (templateId) => {
    setSyncingId(templateId);
    try {
      const updated = await request('post', `/api/templates/${templateId}/sync`);
      setTemplates(prev => prev.map(t => t.id === templateId ? updated : t));
      if (updated.status === 'APPROVED') {
        success(`"${updated.name}" was approved by Meta!`);
      } else if (updated.status === 'REJECTED') {
        toastError(`"${updated.name}" was rejected by Meta.`);
      } else {
        success(`Status still ${updated.status} on Meta's side.`);
      }
    } catch (err) {
      toastError(err.response?.data?.error || 'Failed to sync status from Meta');
    } finally {
      setSyncingId(null);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template? This will also remove it from Meta. This action cannot be undone.')) return;
    try {
      await request('delete', `/api/templates/${templateId}`);
      fetchTemplates();
    } catch (err) {
      console.error('Failed to delete template:', err);
      toastError(err.response?.data?.error || 'Failed to delete template');
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'APPROVED':
        return { label: 'Approved', icon: <CheckCircle size={12} />, className: 'status-approved' };
      case 'PENDING':
        return { label: 'Pending Meta Review', icon: <Clock size={12} />, className: 'status-pending' };
      case 'REJECTED':
        return { label: 'Rejected by Meta', icon: <AlertCircle size={12} />, className: 'status-rejected' };
      case 'PAUSED':
        return { label: 'Paused by Meta', icon: <AlertCircle size={12} />, className: 'status-pending' };
      default:
        return { label: status, icon: null, className: 'status-default' };
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'UTILITY': return '#10B981';
      case 'MARKETING': return '#F59E0B';
      case 'AUTHENTICATION': return '#8B5CF6';
      default: return '#64748B';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'UTILITY': return <Zap size={12} />;
      case 'MARKETING': return <Tag size={12} />;
      case 'AUTHENTICATION': return <Shield size={12} />;
      default: return <FileText size={12} />;
    }
  };

  const getPreviewText = (content) => content.replace(/\{\{(\d+)\}\}/g, (_, n) => `[var${n}]`);

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.content?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || template.status === statusFilter.toUpperCase();
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: templates.length,
    approved: templates.filter(t => t.status === 'APPROVED').length,
    pending: templates.filter(t => t.status === 'PENDING').length,
    rejected: templates.filter(t => t.status === 'REJECTED').length,
  };

  const statusFilters = [
    { value: 'all', label: 'All', count: stats.total },
    { value: 'approved', label: 'Approved', count: stats.approved },
    { value: 'pending', label: 'Pending', count: stats.pending },
    { value: 'rejected', label: 'Rejected', count: stats.rejected },
  ];

  if (loading) {
    return (
      <div className="templates-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="templates-page">
      <div className="templates-header">
        <div>
          <h1 className="templates-title">Message Templates</h1>
          <p className="templates-subtitle">Templates are submitted to Meta for review — approval usually takes a few minutes to a few hours</p>
        </div>
        <Button onClick={openCreateModal} className="btn-create">
          <Plus size={16} />
          Create Template
        </Button>
      </div>

      <div className="templates-stats">
        <div className="stat-item">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Templates</div>
        </div>
        <div className="stat-item">
          <div className="stat-value" style={{ color: '#10B981' }}>{stats.approved}</div>
          <div className="stat-label">Approved</div>
        </div>
        <div className="stat-item">
          <div className="stat-value" style={{ color: '#F59E0B' }}>{stats.pending}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-item">
          <div className="stat-value" style={{ color: '#EF4444' }}>{stats.rejected}</div>
          <div className="stat-label">Rejected</div>
        </div>
      </div>

      <div className="templates-toolbar">
        <div className="status-filters">
          {statusFilters.map((filter) => (
            <button
              key={filter.value}
              className={`status-filter ${statusFilter === filter.value ? 'active' : ''}`}
              onClick={() => setStatusFilter(filter.value)}
            >
              {filter.label}
              <span className="filter-count">{filter.count}</span>
            </button>
          ))}
        </div>
        <div className="search-wrapper">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="templates-grid">
        {filteredTemplates.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} className="empty-icon" />
            <h3>No templates found</h3>
            <p>
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first message template to start sending campaigns'}
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <Button onClick={openCreateModal}>Create Template</Button>
            )}
          </div>
        ) : (
          filteredTemplates.map((template) => {
            const statusConfig = getStatusConfig(template.status);
            const categoryColor = getCategoryColor(template.category);
            const isApproved = template.status === 'APPROVED';

            return (
              <Card key={template.id} className="template-card">
                <div className="template-card-header">
                  <div className="template-name-section">
                    <div className="template-icon" style={{ color: categoryColor }}>
                      <FileText size={18} />
                    </div>
                    <div>
                      <div className="template-name">{template.name}</div>
                      <div className="template-meta">
                        <span className="template-category" style={{ background: `${categoryColor}15`, color: categoryColor }}>
                          {getCategoryIcon(template.category)}
                          {template.category}
                        </span>
                        <span className="template-language">
                          <Globe size={10} />
                          {template.language?.toUpperCase() || 'EN_US'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="template-actions">
                    {(template.status === 'PENDING' || template.status === 'PAUSED') && (
                      <button
                        className="action-btn sync"
                        onClick={() => handleSync(template.id)}
                        disabled={syncingId === template.id}
                        title="Check latest status from Meta"
                      >
                        <RefreshCw size={16} className={syncingId === template.id ? 'spinning' : ''} />
                        {syncingId === template.id ? 'Checking...' : 'Sync'}
                      </button>
                    )}
                    {isApproved ? (
                      <span className="action-btn locked" title="Approved templates can't be edited">
                        <Lock size={14} />
                      </span>
                    ) : (
                      <button className="action-btn edit" onClick={() => openEditModal(template)} title="Edit">
                        <Edit2 size={16} />
                      </button>
                    )}
                    <button className="action-btn delete" onClick={() => handleDelete(template.id)} title="Delete">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="template-preview">
                  <div className="preview-bubble">
                    {getPreviewText(template.content)}
                  </div>
                  <div className="preview-variables">
                    <Sparkles size={12} />
                    <span>Variables will be replaced with contact data</span>
                  </div>
                </div>

                {template.status === 'REJECTED' && template.rejectionReason && (
                  <div className="rejection-box">
                    <AlertCircle size={14} />
                    <span>{template.rejectionReason}</span>
                  </div>
                )}

                <div className="template-card-footer">
                  <div className={`status-badge ${statusConfig.className}`}>
                    {statusConfig.icon}
                    <span>{statusConfig.label}</span>
                  </div>
                  <div className="template-date">
                    <Clock size={12} />
                    {new Date(template.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Template' : 'Create Template'}>
        <form onSubmit={handleSubmit} className="template-form">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Template Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="payment_reminder"
                hint="Lowercase letters, numbers, underscores only — this cannot be changed after Meta approves it"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                className="form-select"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                required
              >
                <option value="UTILITY">📦 UTILITY - Transactional</option>
                <option value="MARKETING">📢 MARKETING - Promotional</option>
                <option value="AUTHENTICATION">🔐 AUTHENTICATION - OTP/Verification</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Language</label>
            <select
              className="form-select"
              value={form.language}
              onChange={(e) => setForm({ ...form, language: e.target.value })}
              required
            >
              {LANGUAGE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="form-hint">Must match a language code Meta supports — this cannot be freeform text.</p>
          </div>

          <div className="form-group">
            <label className="form-label">Message Body</label>
            <textarea
              className="form-textarea"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Hello {{1}}, your payment of {{2}} TZS is due on {{3}}."
              rows={5}
              required
            />
            <p className="form-hint">
              Use {'{{'}1{'}}'}, {'{{'}2{'}}'} for variables. Variables must be sequential starting from 1.
            </p>
          </div>

          {form.content && (
            <div className="live-preview">
              <div className="live-preview-label">
                <Sparkles size={14} />
                Live Preview
              </div>
              <div className="live-preview-bubble">
                {getPreviewText(form.content)}
              </div>
            </div>
          )}

          {error && <div className="alert error">{error}</div>}

          <div className="modal-actions">
            <Button type="button" variant="ghost" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? 'Update & Resubmit to Meta' : 'Submit to Meta for Review'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Templates;